import { useEffect } from 'react';
import { fireEvent } from 'src/helpers/TagManager';
import { trackMetaEvent } from 'src/helpers/MetaPixelTracking';
import { capturePostHogEvent } from 'src/utils/posthog';

type OutboundLinkType = 'email' | 'whatsapp' | 'phone';

interface LinkConfig {
    type: OutboundLinkType;
    gtmEvent: string;
    metaLeadType: string;
    posthogEvent: string;
}

const LINK_CONFIGS: LinkConfig[] = [
    {
        type: 'email',
        gtmEvent: 'EmailLinkClick',
        metaLeadType: 'email-click',
        posthogEvent: 'email_link_click'
    },
    {
        type: 'whatsapp',
        gtmEvent: 'WhatsAppLinkClick',
        metaLeadType: 'whatsapp-click',
        posthogEvent: 'whatsapp_link_click'
    },
    {
        type: 'phone',
        gtmEvent: 'PhoneLinkClick',
        metaLeadType: 'phone-click',
        posthogEvent: 'phone_link_click'
    }
];

const getLinkConfig = (href: string): LinkConfig | undefined => {
    if (href.startsWith('mailto:')) return LINK_CONFIGS[0];
    if (href.includes('://wa.me')) return LINK_CONFIGS[1];
    if (href.startsWith('tel:')) return LINK_CONFIGS[2];
    return undefined;
};

/**
 * Event delegation hook that detects clicks on outbound contact links
 * (mailto:, tel:, wa.me) and fires GTM, Meta Pixel + CAPI, and PostHog events.
 *
 * Call once from the root container so tracking is active site-wide.
 */
export const useOutboundLinkTracking = (): void => {
    useEffect(() => {
        const handleClick = (event: MouseEvent): void => {
            if (!(event.target instanceof Element)) return;
            const anchor = event.target.closest('a[href]') as HTMLAnchorElement | null;
            if (!anchor) return;

            const href = anchor.getAttribute('href') ?? '';
            const config = getLinkConfig(href);
            if (!config) return;

            // 1. GTM dataLayer
            fireEvent({
                event: config.gtmEvent,
                props: { link_url: href }
            });

            // 2. Meta Pixel + Netlify CAPI
            trackMetaEvent({
                eventName: 'Lead',
                eventData: {
                    lead_type: config.metaLeadType,
                    content_category: 'lead',
                    content_name: config.type
                }
            });

            // 3. PostHog
            capturePostHogEvent(config.posthogEvent, {
                link_url: href
            });
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, []);
};
